import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { article, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/guides/mcp')({
  component: McpGuidePage,
  head: () => ({
    meta: [
      { title: 'Uptrack MCP Server: Connect Claude to Your Monitoring' },
      { name: 'description', content: 'Connect Claude to Uptrack over MCP. List monitors, create checks, acknowledge incidents and read uptime. OAuth sign-in, 16 tools. Needs a paid plan.' },
      { property: 'og:title', content: 'Uptrack MCP Server — Uptrack' },
      { property: 'og:description', content: 'Manage uptime monitors, incidents and alert channels from Claude. OAuth sign-in, no API keys.' },
      { property: 'og:image', content: 'https://uptrack.app/og/ai.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/guides/mcp' },
    ],
    scripts: [
      jsonLdScript(
        article({
          headline: 'Uptrack MCP Server: Connect Claude to Your Monitoring',
          description: 'Connect Claude to Uptrack over MCP. List monitors, create checks, acknowledge incidents and read uptime. OAuth sign-in, 16 tools. Needs a paid plan.',
          url: 'https://uptrack.app/guides/mcp',
          datePublished: '2026-10-09',
        }),
      ),
    ],
  }),
})

const code = 'rounded bg-muted px-1.5 py-0.5 font-mono text-sm'

const TOOLS: { name: string; does: string; scope: string; kind: 'read' | 'write' | 'delete' }[] = [
  { name: 'list_monitors', does: 'List monitors with id, name, URL and status.', scope: 'monitors:read', kind: 'read' },
  { name: 'get_monitor', does: 'Get one monitor with its current status and 30-day uptime.', scope: 'monitors:read', kind: 'read' },
  { name: 'get_monitor_analytics', does: 'Uptime percentage, daily uptime and response-time percentiles (30 days by default).', scope: 'analytics:read', kind: 'read' },
  { name: 'get_monitor_checks', does: 'Recent check results for one monitor: time, status, HTTP code, response time and error.', scope: 'monitors:read', kind: 'read' },
  { name: 'check_website', does: 'Check any public URL right now from several regions. No monitor needed.', scope: 'monitors:read', kind: 'read' },
  { name: 'get_dashboard_stats', does: 'Monitor counts, ongoing incidents and 30-day uptime for the organization.', scope: 'analytics:read', kind: 'read' },
  { name: 'list_incidents', does: 'List recent incidents (10 by default).', scope: 'incidents:read', kind: 'read' },
  { name: 'list_status_pages', does: 'List the organization’s status pages.', scope: 'status_pages:read', kind: 'read' },
  { name: 'list_alert_channels', does: 'List alert channels: id, name, type and whether active.', scope: 'alerts:read', kind: 'read' },
  { name: 'create_monitor', does: 'Create a monitor. A URL and a name are required. Default type is HTTP with a 180-second interval.', scope: 'monitors:write', kind: 'write' },
  { name: 'update_monitor', does: 'Change a monitor’s name, URL or check interval.', scope: 'monitors:write', kind: 'write' },
  { name: 'pause_monitor', does: 'Pause a monitor. Safe to repeat.', scope: 'monitors:write', kind: 'write' },
  { name: 'resume_monitor', does: 'Resume a paused monitor. Safe to repeat.', scope: 'monitors:write', kind: 'write' },
  { name: 'acknowledge_incident', does: 'Acknowledge an incident. This pauses escalation reminders.', scope: 'incidents:write', kind: 'write' },
  { name: 'create_alert_channel', does: 'Create an email, Slack, Discord or Telegram alert channel.', scope: 'alerts:write', kind: 'write' },
  { name: 'delete_monitor', does: 'Delete a monitor by id. This is the only destructive tool.', scope: 'monitors:write', kind: 'delete' },
]

const PROMPTS = [
  'List my monitors and tell me which ones are down.',
  'Check https://example.com from several regions right now.',
  'Show the last checks for the API monitor and any errors.',
  'Show uptime and response times for my API monitor over the last 30 days.',
  'Create an HTTP monitor named “Marketing site” for https://example.com.',
  'Pause the “Staging” monitor while I deploy, then resume it.',
  'Acknowledge the open incident on checkout.',
  'Create an email alert channel named “On-call” for oncall@example.com.',
]

function McpGuidePage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <h1 className="font-sans mb-4 text-4xl font-semibold">Uptrack MCP server</h1>
      <p className="mb-8 text-lg leading-[1.7] text-body">
        Connect Claude to Uptrack and manage your uptime monitoring in plain language. List monitors,
        create checks, read uptime and acknowledge incidents without opening the dashboard.
      </p>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Connect Claude</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Uptrack is{' '}
          <a className="text-primary hover:underline" href="https://claude.ai/directory/uptrack" target="_blank" rel="noopener noreferrer">listed in Claude&rsquo;s connector directory</a>
          {' '}with the Community label. Anthropic screens Community connectors automatically but has not reviewed them in depth.
          Read what{' '}
          <a className="text-primary hover:underline" href="https://claude.com/docs/connectors/verification" target="_blank" rel="noopener noreferrer">the labels mean</a>.
          The connector needs an Uptrack account on a paid plan. The free plan does not include it.
        </p>
        <h3 className="font-sans mb-3 text-xl font-semibold">Claude on the web, desktop and mobile</h3>
        <div className="space-y-6">
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">1</div>
            <div>
              <h3 className="font-sans mb-1 font-semibold">Add the connector</h3>
              <p className="text-lg leading-[1.7] text-body">
                In Claude, open <strong>Customize → Connectors</strong>, find <strong>Uptrack</strong> in the directory and choose <strong>Connect</strong>.
                If you do not see it, choose <strong>Add custom connector</strong> and use this server URL:
              </p>
              <pre tabIndex={0} className="mt-3 overflow-x-auto rounded-md border border-border bg-background p-4 font-mono text-sm text-foreground"><code>https://api.uptrack.app/mcp</code></pre>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">2</div>
            <div>
              <h3 className="font-sans mb-1 font-semibold">Sign in and approve</h3>
              <p className="text-lg leading-[1.7] text-body">
                Claude opens the Uptrack consent page. Sign in to your Uptrack account and choose <strong>Allow</strong>.
                You need an Uptrack account on a paid plan. You do not need an API key or a client secret.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">3</div>
            <div>
              <h3 className="font-sans mb-1 font-semibold">Ask</h3>
              <p className="text-lg leading-[1.7] text-body">
                Turn the Uptrack connector on in a chat and ask a question. Claude asks you to approve each tool call
                unless you choose “Always allow”.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Claude Code</h2>
        <p className="mb-3 text-lg leading-[1.7] text-body">
          Add the server as a remote HTTP server:
        </p>
        <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-md border border-border bg-background p-4 font-mono text-sm text-foreground"><code>claude mcp add --transport http uptrack https://api.uptrack.app/mcp</code></pre>
        <p className="mb-3 text-lg leading-[1.7] text-body">
          Then run <code className={code}>/mcp</code> inside Claude Code and follow the steps in your browser.
          Sign in to Uptrack and choose <strong>Allow</strong>. From a shell you can run <code className={code}>claude mcp login uptrack</code> instead.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          Other clients that support remote MCP servers and OAuth can use the same URL.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Example prompts</h2>
        <ul className="space-y-2 text-lg leading-[1.7] text-body">
          {PROMPTS.map((p) => (
            <li key={p} className="rounded-lg border border-border bg-card px-4 py-3">{p}</li>
          ))}
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Tools</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          The server has 16 tools. Nine only read data. Six change data. One deletes a monitor. Every tool
          has a title and read-only, destructive and idempotent hints, and only <code className={code}>delete_monitor</code> is marked destructive.
          Every tool is limited to the organization you signed in with, and each needs an OAuth scope that you approve on the consent page.
        </p>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Tool</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">What it does</th>
                <th className="pb-3 text-left font-medium text-muted-foreground">Scope</th>
              </tr>
            </thead>
            <tbody>
              {TOOLS.map((t) => (
                <tr key={t.name} className="border-b align-top">
                  <td className="py-3 pr-4">
                    <code className={code}>{t.name}</code>
                    {t.kind === 'delete' && <span className="ml-2 text-xs font-medium text-status-down">destructive</span>}
                  </td>
                  <td className="py-3 pr-4 text-body">{t.does}</td>
                  <td className="py-3"><code className="font-mono text-xs">{t.scope}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Security and data</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>Sign-in uses OAuth 2.0 with PKCE (S256). Clients register by Client ID Metadata Document or by dynamic client registration.</li>
          <li>The organization comes from your access token. A tool call cannot read or change another organization.</li>
          <li>Tokens carry only the scopes you approve. A read-only token cannot call a write tool.</li>
          <li>Uptrack returns monitor, incident and alert-channel data that your account already holds. It does not receive your conversation.</li>
          <li>To disconnect, remove the connector in Claude.</li>
        </ul>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          Read the <Link to="/privacy" className="text-primary hover:underline">privacy policy</Link> for how Uptrack handles data.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">For developers</h2>
        <p className="mb-3 text-lg leading-[1.7] text-body">
          The server speaks MCP over stateless Streamable HTTP at <code className={code}>https://api.uptrack.app/mcp</code>.
          It also answers on <code className={code}>/api/mcp</code>. Discovery documents:
        </p>
        <ul className="list-disc space-y-1 pl-6 text-lg leading-[1.7] text-body">
          <li><a className="text-primary hover:underline" href="https://api.uptrack.app/.well-known/oauth-protected-resource/mcp">/.well-known/oauth-protected-resource/mcp</a></li>
          <li><a className="text-primary hover:underline" href="https://api.uptrack.app/.well-known/oauth-authorization-server">/.well-known/oauth-authorization-server</a></li>
        </ul>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          Prefer plain HTTP? Use the <Link to="/docs" className="text-primary hover:underline">REST API</Link>.
        </p>
      </section>

      <section id="api-key" className="mb-12 scroll-mt-24">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Use the npm package with an API key</h2>
        <p className="text-lg leading-[1.7] text-body">
          Use the <code className={code}>@uptrack-app/mcp</code> package when your client cannot reach a remote MCP server, for example in CI.
          Create a key in the dashboard under <strong>Settings → API Keys</strong>, then set it as <code className={code}>UPTRACK_API_KEY</code>.
          API keys need a paid plan.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
        <div className="space-y-6">
          <div>
            <h3 className="font-sans mb-1 font-semibold">Does it work with other AI clients?</h3>
            <p className="text-lg leading-[1.7] text-body">
              Yes. Any client that supports remote MCP servers and OAuth can use the same URL.
            </p>
          </div>
          <div>
            <h3 className="font-sans mb-1 font-semibold">Can Claude delete my monitors?</h3>
            <p className="text-lg leading-[1.7] text-body">
              Only the <code className={code}>delete_monitor</code> tool can, and only with the <code className={code}>monitors:write</code> scope.
              Claude asks for your approval before each call. Do not choose “Always allow” for that tool if you want a check every time.
            </p>
          </div>
          <div>
            <h3 className="font-sans mb-1 font-semibold">I changed my monitors, but Claude shows old tools.</h3>
            <p className="text-lg leading-[1.7] text-body">
              Claude caches the tool list. Open the connector settings and choose “Refresh tools list”.
            </p>
          </div>
          <div>
            <h3 className="font-sans mb-1 font-semibold">Where do I get help?</h3>
            <p className="text-lg leading-[1.7] text-body">
              Email <a className="text-primary hover:underline" href="mailto:support@uptrack.app">support@uptrack.app</a>.
            </p>
          </div>
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Monitor from your chat</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Pro includes 50 monitors at 30-second checks from $3/mo, with a 30-day money-back guarantee.
          Read the{' '}
          <Link to="/blog/uptrack-in-claude-connector-directory" className="text-primary hover:underline">announcement post</Link>.
        </p>
        <Button asChild size="lg">
          <Link to="/pricing">
            See plans <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </article>
  )
}
